<script setup lang="ts">
//#region imports
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { computed, nextTick, ref } from 'vue';
import { omit } from 'lodash-es';
import { useApi } from '@/api';
import { usePermissions } from '@/composables/usePermissions';
import { useTableFilter, useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useEventBus } from '@/setup/eventBus';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useLodash } from '@/setup/lodash';
import { useDictList } from '@/composables/useDictList';
import type {
  GetDomainsManagementDnsHostingAccountsResponse,
  GetDomainsManagementPlatformServersResponse,
  GetDomainsManagementPlatformServiceDomainsData,
  GetDomainsManagementPlatformServiceDomainsResponse,
  GetDomainsManagementPlatformServiceOriginsResponse,
  GetDomainsManagementRegistrarAccountsResponse,
} from '@/types/openapi-ts';
import type { SettingsPageQuery } from '@/types/core';
import type { UiTableNewField, UiTableNewFilter, UiTableNewSortEmit } from '@/types/table';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type ServiceDomainsQuery = SettingsPageQuery<GetDomainsManagementPlatformServiceDomainsData['query']>;

type RequestType = 'getServiceOrigins' | 'getPlatformServers' | 'getDomainRegistrarAccounts' | 'getDNSHostingAccounts';

type RowType = GetDomainsManagementPlatformServiceDomainsResponse['payload'][number];

interface OptionsData {
  platformServiceOriginList: GetDomainsManagementPlatformServiceOriginsResponse['payload'];
  platformServerList: GetDomainsManagementPlatformServersResponse['payload'];
  domainRegistrarAccountList: GetDomainsManagementRegistrarAccountsResponse['payload'];
  dnsHostingAccountList: GetDomainsManagementDnsHostingAccountsResponse['payload'];
}
//#endregion

//#region const
const LS_PATH = 'ServiceDomains';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<GetDomainsManagementPlatformServiceDomainsResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'domain_name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'setup_mode',
    renderValue: (value) => t(`domainManagement.popup.form.setup_mode.${value}`),
  },
  {
    name: 'platform_service_origin_name',
    sortable: 'custom',
  },
  {
    name: 'platform_server_name',
    sortable: 'custom',
  },
  {
    name: 'used_in_groups_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'blocked_countries_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
    click: openBlockedCountriesPopup,
    class: 't:cursor-pointer positive',
  },
  {
    name: 'domain_registrar_account_name',
    sortable: 'custom',
    computedValue: (row) => row.domain_registrar_account_name || '—',
  },
  {
    name: 'dns_hosting_account_name',
    sortable: 'custom',
    computedValue: (row) => row.dns_hosting_account_name || '—',
  },
  {
    name: 'ns_records_state',
    sortable: 'custom',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.ns_records_state.values.${value}`),
    status: (value) => value,
  },
  {
    name: 'dns_records_state',
    sortable: 'custom',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.dns_records_state.values.${value}`),
    status: (value) => value,
  },
  {
    name: 'ssl_certificate_state',
    sortable: 'custom',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.ssl_certificate_state.values.${value}`),
    status: (value) => value,
  },
  {
    name: 'traffic_last_30_days_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'traffic_last_7_days_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'traffic_last_day_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'created_at',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
];
const canBeUndefined = [
  'ns_management',
  'dns_management',
  'ssl_issuance',
  'used_in_any_group',
  'blocked_in_any_country',
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const updateUrl = useUpdateUrl();
const { hasPermission } = usePermissions();
const { t } = useI18n();
const _ = useLodash();
const { yesNoList, allAutoManualList  } = useDictList();
const { searchQuery } = useTableSearch();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
});
const popupManager = usePopupManager();
const eventBus = useEventBus();
//#endregion

//#region state
const tableData = ref<GetDomainsManagementPlatformServiceDomainsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);

const isSelectLoading = ref(false);
const options = ref<OptionsData>({
  platformServiceOriginList: [],
  platformServerList: [],
  domainRegistrarAccountList: [],
  dnsHostingAccountList: [],
});
//#endregion

//#region computed
const canEditServiceDomain = computed(() => {
  return hasPermission('platform_service_domains_edit');
});
const canRunJob = computed(() => {
  return hasPermission('platform_service_domains_can_run_jobs');
});

const selectComponentCommonParams = computed(() => {
  return {
    loading: isSelectLoading.value,
    remote: true,
  };
});

const filtersList = computed(() => {
  return [
    {
      id: 'ns_management',
      name: 'nsManagement',
      type: 'radio',
      params: {
        options: allAutoManualList.value,
      },
    },
    {
      id: 'dns_management',
      name: 'dnsManagement',
      type: 'radio',
      params: {
        options: allAutoManualList.value,
      },
    },
    {
      id: 'ssl_issuance',
      name: 'sslIssuance',
      type: 'radio',
      params: {
        options: allAutoManualList.value,
      },
    },
    {
      id: 'platform_service_origin_id',
      name: 'platformServiceOrigin',
      type: 'select',
      params: {
        list: options.value.platformServiceOriginList,
        'remote-method': (searchVal: string) => getSelectList('getServiceOrigins', 'platformServiceOriginList', searchVal),
        ...selectComponentCommonParams.value,
      },
    },
    {
      id: 'platform_server_id',
      name: 'platformServer',
      permission: 'platform_servers_view',
      type: 'select',
      params: {
        list: options.value.platformServerList,
        'remote-method': (searchVal: string) => getSelectList('getPlatformServers', 'platformServerList', searchVal),
        ...selectComponentCommonParams.value,
      },
    },
    {
      id: 'domain_registrar_account_id',
      name: 'domainRegistrarAccount',
      permission: 'domain_registrar_accounts_view',
      type: 'select',
      params: {
        list: options.value.domainRegistrarAccountList,
        'remote-method': (searchVal: string) => getSelectList('getDomainRegistrarAccounts', 'domainRegistrarAccountList', searchVal),
        ...selectComponentCommonParams.value,
      },
    },
    {
      id: 'dns_hosting_account_id',
      name: 'dnsHostingAccount',
      permission: 'dns_hosting_accounts_view',
      type: 'select',
      params: {
        list: options.value.dnsHostingAccountList,
        'remote-method': (searchVal: string) => getSelectList('getDNSHostingAccounts', 'dnsHostingAccountList', searchVal),
        ...selectComponentCommonParams.value,
      },
    },
    {
      id: 'used_in_any_group',
      name: 'usedInAnyGroup',
      type: 'radio',
      params: {
        options: yesNoList.value,
      },
    },
    {
      id: 'blocked_in_any_country',
      name: 'blockedInAnyCountry',
      type: 'radio',
      params: {
        options: yesNoList.value,
      },
    },
  ];
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    setFilter(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    Object.assign(optionsSort.value, _.pick(_urlData, Object.keys(optionsSort.value)));
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
  } as GetDomainsManagementPlatformServiceDomainsData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: ServiceDomainsQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();
  options = {
    ...options,
    ...filterParams.value,
  };

  try {
    const { data: { payload, misc } } = await api.getServiceDomains(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

async function openServiceDomainPopup(id = null) {
  popupManager.serviceDomainPopup.open({
    data: {
      domainId: id,
    },
    listeners: {
      success: () => {
        fetchData(true);
      },
    },
  });
}

function handleDelete(row: RowType) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('domainManagement.buttons.delete'),
      message: t('domainManagement.messages.delete', { name: row.domain_name}),
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('domainManagement.buttons.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteServiceDomain(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}

async function handleReCheck(row: RowType) {
  const { status } = await api.reCheckServiceDomain(row.id);
  if (status === 204) {
    eventBus.$emit('notify-event', {
      type: 'info',
      text: t('domainManagement.messages.success'),
    });
    await fetchData();
  } else {
    eventBus.$emit('notify-event', {
      type: 'error',
      text: t('domainManagement.messages.failed'),
    });
  }
}

function openBlockedCountriesPopup(row: RowType) {
  popupManager.blockedCountriesPopup.open({
    data: {
      domainId: row.id,
      canEditServiceDomain: canEditServiceDomain.value,
    },
    listeners: {
      update: () => {
        fetchData(true);
      },
    },
  });
}

function getServiceDomainsFilter(data: UiTableNewFilter['filter']) {
  filterParams.value = data;
  handleRefresh();
}

const updateOptions = <K extends keyof OptionsData>(key: K, payload: OptionsData[K]) => {
  options.value[key] = payload;
};

async function getSelectList(request: RequestType, list: keyof OptionsData, searchValue: string) {
  isSelectLoading.value = true;

  const query = {
    search: searchValue || undefined,
    limit: 25,
  };

  try {
    const response = await api[request](query);
    updateOptions(list, response.data.payload);
  } catch (error) {
    console.error(error);
  } finally {
    isSelectLoading.value = false;
  }
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="service-domains">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('domainManagement.pageTitle.serviceDomains') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <DefaultFilter
          :model-value="filterParams"
          :filters-list="filtersList"
          :can-be-undefined="canBeUndefined"
          @submit="getServiceDomainsFilter"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="canEditServiceDomain"
          filled
          icon="plus"
          @click="openServiceDomainPopup()"
        >
          {{ $t('domainManagement.buttons.add_service_domain') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="domainManagement"
      @sort="handleSort"
    >
      <template
        v-if="canEditServiceDomain || canRunJob"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="100"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              v-if="canRunJob"
              icon="history"
              color="var(--text-info)"
              :tooltip="$t('domainManagement.buttons.recheck')"
              @click="handleReCheck(scope.row)"
            />
            <ActionIcon
              v-if="canEditServiceDomain"
              icon="pen"
              :tooltip="$t('domainManagement.buttons.edit')"
              :disabled="scope.row.setup_mode === 'manual'"
              @click="openServiceDomainPopup(scope.row.id)"
            />
            <ActionIcon
              v-if="canEditServiceDomain"
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('domainManagement.buttons.delete')"
              :disabled="scope.row.used_in_groups_count > 0"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
